import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Anchor,
  DollarSign,
  FileText,
  Image,
  Mail,
  Package,
  RotateCcw,
  Save,
  Search,
  ShoppingCart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@marine/ui/alert-dialog';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Checkbox } from '@marine/ui/checkbox';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Skeleton } from '@marine/ui/skeleton';
import { Slider } from '@marine/ui/slider';
import { Textarea } from '@marine/ui/textarea';
import { AiModelSelect } from '@marine/ui/AiModelSelect';
import {
  usePlatformAIModels,
  useRefreshPlatformAIModels,
} from '@/modules/platform/hooks/usePlatformAIModels';
import {
  useCustomizeTenantAiPrompt,
  useRevertTenantAiPrompt,
  useTenantAiPrompts,
  type TenantAIPrompt,
  type UpsertTenantAIPromptInput,
} from '@/modules/platform/services/tenantAiAdminApi';

// ---------------------------------------------------------------------------
// Module & category config — mirrors the (now-removed) tenant
// AiPromptEditorTab.tsx so the two editors read as the same shape.
// ---------------------------------------------------------------------------

const MODULE_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  inventory: { label: 'Inventory', icon: Anchor },
  crm: { label: 'CRM', icon: ShoppingCart },
  service: { label: 'Service', icon: Package },
};

const MODULE_ORDER = ['inventory', 'crm', 'service'];

const CATEGORY_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  description: { label: 'Description', icon: FileText },
  specs: { label: 'Specs', icon: Search },
  image_analysis: { label: 'Image Analysis', icon: Image },
  comparables: { label: 'Comparables', icon: DollarSign },
  crm: { label: 'Quotes & Email', icon: Mail },
  seo: { label: 'SEO', icon: Search },
  trade_in: { label: 'Trade-In', icon: DollarSign },
};

const CATEGORY_ORDER = [
  'description',
  'specs',
  'image_analysis',
  'comparables',
  'seo',
  'trade_in',
  'crm',
];

// ---------------------------------------------------------------------------
// Form values
// ---------------------------------------------------------------------------

interface PromptFormValues {
  system_prompt: string;
  user_prompt_template: string;
  model_override: string;
  temperature_override: number;
  max_tokens_override: number;
  override_temperature: boolean;
  override_max_tokens: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ModuleGroup {
  module: string;
  categories: Record<string, TenantAIPrompt[]>;
}

/** Platform-default rows have `id: null`; the composite (module,key) is the
 * only value that's always present and unique, so it's the
 * selection/React-key identity for the whole tab (mirrors #867 D-02). */
function promptKeyOf(prompt: Pick<TenantAIPrompt, 'module' | 'key'>): string {
  return `${prompt.module}:${prompt.key}`;
}

function groupByModuleAndCategory(prompts: TenantAIPrompt[]): ModuleGroup[] {
  const moduleMap: Record<string, Record<string, TenantAIPrompt[]>> = {};

  for (const p of prompts) {
    const mod = p.module ?? 'inventory';
    const cat = p.metadata?.category ?? 'other';
    if (!moduleMap[mod]) moduleMap[mod] = {};
    if (!moduleMap[mod][cat]) moduleMap[mod][cat] = [];
    moduleMap[mod][cat].push(p);
  }

  for (const mod of Object.keys(moduleMap)) {
    for (const cat of Object.keys(moduleMap[mod])) {
      moduleMap[mod][cat].sort((a, b) => a.sort_order - b.sort_order);
    }
  }

  const result: ModuleGroup[] = [];
  for (const mod of MODULE_ORDER) {
    if (moduleMap[mod]) {
      result.push({ module: mod, categories: moduleMap[mod] });
    }
  }
  for (const mod of Object.keys(moduleMap)) {
    if (!MODULE_ORDER.includes(mod)) {
      result.push({ module: mod, categories: moduleMap[mod] });
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TenantAiPromptsSectionProps {
  tenantId: string;
}

/**
 * Platform → tenant detail → AI Prompts tab (#1029). Per-tenant equivalent
 * of the deleted dealer-facing AiPromptEditorTab.tsx — same master/detail
 * editor shape, but every save/revert writes into THIS tenant's own
 * `ai_prompts` rows through the acting-on-tenant repository seam (no cache
 * wait), and every model comes from the platform-scoped OpenRouter catalog,
 * not the tenant one.
 */
export function TenantAiPromptsSection({ tenantId }: TenantAiPromptsSectionProps) {
  const { data: prompts, isLoading: promptsLoading, isError: promptsError } = useTenantAiPrompts(tenantId);
  const customizeMutation = useCustomizeTenantAiPrompt(tenantId);
  const revertMutation = useRevertTenantAiPrompt(tenantId);
  const { data: models, isLoading: modelsLoading } = usePlatformAIModels();
  const refreshModelsMutation = useRefreshPlatformAIModels();

  const [selectedPromptKey, setSelectedPromptKey] = useState<string | null>(null);

  const cursorPosition = useRef<{ field: string; start: number; end: number }>({
    field: '',
    start: 0,
    end: 0,
  });

  const form = useForm<PromptFormValues>({
    defaultValues: {
      system_prompt: '',
      user_prompt_template: '',
      model_override: '__none__',
      temperature_override: 0.7,
      max_tokens_override: 4096,
      override_temperature: false,
      override_max_tokens: false,
    },
  });

  const promptList = prompts ?? [];
  const moduleGroups = groupByModuleAndCategory(promptList);
  const selectedPrompt = promptList.find((p) => promptKeyOf(p) === selectedPromptKey) ?? null;

  useEffect(() => {
    if (!selectedPrompt) return;
    form.reset({
      system_prompt: selectedPrompt.system_prompt,
      user_prompt_template: selectedPrompt.user_prompt_template,
      model_override: selectedPrompt.model_override ?? '__none__',
      temperature_override: selectedPrompt.temperature_override ?? 0.7,
      max_tokens_override: selectedPrompt.max_tokens_override ?? 4096,
      override_temperature: selectedPrompt.temperature_override !== null,
      override_max_tokens: selectedPrompt.max_tokens_override !== null,
    });
    cursorPosition.current = { field: '', start: 0, end: 0 };
  }, [selectedPromptKey, selectedPrompt, form]);

  useEffect(() => {
    if (!selectedPromptKey && promptList.length > 0) {
      setSelectedPromptKey(promptKeyOf(promptList[0]));
    }
  }, [promptList, selectedPromptKey]);

  // ---------------------------------------------------------------------
  // Variable insertion
  // ---------------------------------------------------------------------

  const insertVariable = (variable: string) => {
    const { field, start, end } = cursorPosition.current;
    if (!field) return;
    const current = (form.getValues(field as keyof PromptFormValues) as string) || '';
    const insertion = `{{${variable}}}`;
    const newValue = current.slice(0, start) + insertion + current.slice(end);
    form.setValue(field as keyof PromptFormValues, newValue, { shouldDirty: true });
    cursorPosition.current = {
      field,
      start: start + insertion.length,
      end: start + insertion.length,
    };
  };

  const trackCursor = (field: string) => ({
    onSelect: (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      cursorPosition.current = { field, start: el.selectionStart, end: el.selectionEnd };
    },
    onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      cursorPosition.current = { field, start: el.selectionStart, end: el.selectionEnd };
    },
    onClick: (e: React.MouseEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      cursorPosition.current = { field, start: el.selectionStart, end: el.selectionEnd };
    },
  });

  // ---------------------------------------------------------------------
  // Save & Revert
  // ---------------------------------------------------------------------

  const handleSave = () => {
    if (!selectedPrompt) return;
    const values = form.getValues();

    const input: UpsertTenantAIPromptInput = {
      system_prompt: values.system_prompt,
      user_prompt_template: values.user_prompt_template,
      model_override: values.model_override === '__none__' ? null : values.model_override,
      temperature_override: values.override_temperature ? values.temperature_override : null,
      max_tokens_override: values.override_max_tokens ? values.max_tokens_override : null,
    };

    customizeMutation.mutate(
      { module: selectedPrompt.module, key: selectedPrompt.key, input },
      {
        onSuccess: () => toast.success('Prompt customized for this dealer'),
        onError: () => toast.error('Failed to update prompt'),
      },
    );
  };

  const handleRevert = () => {
    if (!selectedPrompt) return;
    revertMutation.mutate(
      { module: selectedPrompt.module, key: selectedPrompt.key },
      {
        onSuccess: () => toast.success('Reverted to the platform default'),
        onError: () => toast.error('Failed to revert prompt'),
      },
    );
  };

  const isSaving = customizeMutation.isPending || revertMutation.isPending;

  // ---------------------------------------------------------------------
  // Render: Sidebar
  // ---------------------------------------------------------------------

  const renderSidebar = () => (
    <div className="space-y-5">
      {promptsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-7 w-full" />
          ))}
        </div>
      ) : (
        moduleGroups.map((group) => {
          const modConfig = MODULE_LABELS[group.module];
          const ModIcon = modConfig?.icon ?? FileText;
          const modLabel = modConfig?.label ?? group.module;

          const catKeys = CATEGORY_ORDER.filter((cat) => group.categories[cat]?.length);
          for (const cat of Object.keys(group.categories)) {
            if (!catKeys.includes(cat)) catKeys.push(cat);
          }

          return (
            <div key={group.module} className="space-y-3">
              <div className="flex items-center gap-1.5 px-2">
                <ModIcon className="size-3.5 text-muted-foreground" />
                <span className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                  {modLabel}
                </span>
              </div>

              {catKeys.map((cat) => {
                const catConfig = CATEGORY_LABELS[cat];
                const label = catConfig?.label ?? cat;
                const catPrompts = group.categories[cat] ?? [];

                return (
                  <div key={cat} className="space-y-0.5">
                    <div className="px-2 py-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                        {label}
                      </span>
                    </div>
                    {catPrompts.map((prompt) => {
                      const promptKey = promptKeyOf(prompt);
                      return (
                        <button
                          key={promptKey}
                          type="button"
                          onClick={() => setSelectedPromptKey(promptKey)}
                          title={prompt.is_active ? undefined : 'Inactive — toggle on to use this prompt'}
                          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent ${
                            selectedPromptKey === promptKey ? 'bg-accent font-medium' : ''
                          } ${prompt.is_active ? '' : 'text-muted-foreground'}`}
                        >
                          <span className="flex-1 truncate">{prompt.name}</span>
                          {!prompt.is_active && (
                            <span className="shrink-0 rounded-sm bg-muted px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                              Off
                            </span>
                          )}
                          {prompt.is_customized && (
                            <span
                              className="size-1.5 shrink-0 rounded-full bg-primary"
                              title="Customized for this dealer"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );

  // ---------------------------------------------------------------------
  // Render: Detail Panel
  // ---------------------------------------------------------------------

  const renderDetailPanel = () => {
    if (!selectedPrompt) {
      return (
        <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">Select a prompt template to edit</p>
        </div>
      );
    }

    const watchTemp = form.watch('temperature_override');
    const overrideTemp = form.watch('override_temperature');
    const overrideMaxTokens = form.watch('override_max_tokens');

    const modConfig = MODULE_LABELS[selectedPrompt.module ?? 'inventory'];

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{selectedPrompt.name}</h3>
            {modConfig && (
              <Badge variant="outline" size="sm">
                {modConfig.label}
              </Badge>
            )}
            {selectedPrompt.is_customized ? (
              <Badge variant="success" appearance="light" size="sm">
                Customized for this dealer
              </Badge>
            ) : (
              <Badge variant="secondary" size="sm">
                Using platform default
              </Badge>
            )}
            {!selectedPrompt.is_active && (
              <Badge variant="secondary" size="sm">
                Inactive
              </Badge>
            )}
          </div>
          {selectedPrompt.description && (
            <p className="text-2sm text-muted-foreground">{selectedPrompt.description}</p>
          )}
          {!selectedPrompt.is_customized && (
            <p className="text-2sm text-muted-foreground">
              Not customized yet — this dealer is using the platform default. Saving creates a
              dealer-specific copy that only affects this tenant.
            </p>
          )}
          {!selectedPrompt.is_active && (
            <p className="text-2sm rounded-md border border-dashed border-muted-foreground/30 bg-muted/40 px-2 py-1.5 text-muted-foreground">
              This prompt is inactive and will not be invoked for this dealer. Edits are saved,
              but the engine returns an error if it tries to use this prompt.
            </p>
          )}
        </div>

        {/* System Prompt */}
        <div className="space-y-1.5">
          <Label htmlFor="system_prompt">System Prompt</Label>
          <Textarea
            id="system_prompt"
            rows={6}
            className="font-mono text-xs"
            {...form.register('system_prompt')}
            {...trackCursor('system_prompt')}
          />
        </div>

        {/* User Prompt Template */}
        <div className="space-y-1.5">
          <Label htmlFor="user_prompt_template">User Prompt Template</Label>
          <Textarea
            id="user_prompt_template"
            rows={8}
            className="font-mono text-xs"
            {...form.register('user_prompt_template')}
            {...trackCursor('user_prompt_template')}
          />
        </div>

        {/* Variable Reference Panel */}
        {selectedPrompt.variables && selectedPrompt.variables.length > 0 && (
          <div className="space-y-2 rounded-lg border border-border p-4">
            <div className="space-y-0.5">
              <h4 className="text-xs font-semibold">Available Variables</h4>
              <p className="text-2xs text-muted-foreground">Click to insert at cursor position</p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selectedPrompt.variables.map((variable) => (
                <Badge
                  key={variable}
                  variant="outline"
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => insertVariable(variable)}
                >
                  {`{{${variable}}}`}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Model Override */}
        <div className="space-y-4 rounded-lg border border-border p-4">
          <h4 className="text-xs font-semibold">Per-template Overrides</h4>

          <AiModelSelect
            label="Model Override"
            value={form.watch('model_override') === '__none__' ? null : form.watch('model_override')}
            onChange={(v) => form.setValue('model_override', v, { shouldDirty: true })}
            models={models}
            isLoading={modelsLoading}
            onRefresh={() => refreshModelsMutation.mutate()}
            isRefreshing={refreshModelsMutation.isPending}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-2xs"
              onClick={() => form.setValue('model_override', '__none__', { shouldDirty: true })}
            >
              Use default model
            </Button>
          </div>

          {/* Temperature Override */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="override_temperature"
                checked={overrideTemp}
                onCheckedChange={(checked) =>
                  form.setValue('override_temperature', Boolean(checked), { shouldDirty: true })
                }
              />
              <Label htmlFor="override_temperature" className="text-xs">
                Override temperature
              </Label>
              {overrideTemp && (
                <span className="ml-auto text-xs font-mono text-muted-foreground">
                  {Number.isFinite(watchTemp) ? watchTemp.toFixed(1) : '0.7'}
                </span>
              )}
            </div>
            {overrideTemp && (
              <Slider
                min={0}
                max={2}
                step={0.1}
                value={[watchTemp]}
                onValueChange={(value) =>
                  form.setValue('temperature_override', value[0], { shouldDirty: true })
                }
              />
            )}
          </div>

          {/* Max Tokens Override */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="override_max_tokens"
                checked={overrideMaxTokens}
                onCheckedChange={(checked) =>
                  form.setValue('override_max_tokens', Boolean(checked), { shouldDirty: true })
                }
              />
              <Label htmlFor="override_max_tokens" className="text-xs">
                Override max tokens
              </Label>
            </div>
            {overrideMaxTokens && (
              <Input
                type="number"
                min={1}
                max={128000}
                value={form.watch('max_tokens_override')}
                onChange={(e) =>
                  form.setValue('max_tokens_override', Number(e.target.value), { shouldDirty: true })
                }
                className="w-40"
              />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={isSaving || !form.formState.isDirty}>
            <Save className="me-1.5 size-3.5" />
            Save Changes
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                disabled={isSaving || !selectedPrompt.is_customized}
                className="text-destructive"
              >
                <RotateCcw className="me-1.5 size-3.5" />
                Revert to default
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revert to the platform default?</AlertDialogTitle>
                <AlertDialogDescription>
                  This dealer&apos;s own text for this prompt will be discarded, and the current
                  platform default from <code>Platform → AI Settings → Prompts</code> will take
                  over immediately. This cannot be undone, but you can always customize it again.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleRevert}>Revert to default</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    );
  };

  if (promptsError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm text-destructive">Failed to load AI prompts for this tenant</p>
        <p className="text-xs text-muted-foreground">Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <div className="rounded-lg border border-border p-4">{renderSidebar()}</div>
      <div>{renderDetailPanel()}</div>
    </div>
  );
}
